import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { forkJoin, Observable } from 'rxjs';
import { FileService } from '../file/file.service';

type ConditionalFlag = boolean | ((x: any) => boolean);

@Injectable()
export class FormService {
    public constructor(private readonly fileService: FileService) {
    }

    public applyFormValues(formGroup: UntypedFormGroup, formValues): void {
        Object.keys(formValues).forEach(
            (key) => {
                const formControl = <UntypedFormControl>formGroup.controls[key];
                if (formControl instanceof UntypedFormGroup) {
                    this.applyFormValues(formControl, formValues[key]);

                    return true;
                }

                formControl.setValue(formValues[key]);
            });
    }

    public markAllAsTouchedAndDirty(
        control: UntypedFormGroup | UntypedFormArray | UntypedFormControl,
        isIgnoreEmptyValue = true,
        isTouched = true,
        isDirty = true,
    ): void {
        if (control instanceof UntypedFormControl) {
            // Handle FormControl directly
            if (typeof control.value === 'boolean') {
                return; // Skip boolean values as per your logic
            }

            if (isIgnoreEmptyValue && !control.value) {
                return; // Skip controls with empty values
            }

            if (isTouched) {
                control.markAsTouched();
            }

            if (isDirty) {
                control.markAsDirty();
            }

            // Update validity for this control
            // Only call updateValueAndValidity if validation is absolutely necessary
            control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            return;
        }

        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
            // Iterate through controls if it's a FormGroup or FormArray
            Object.keys(control.controls).forEach((key) => {
                const childControl = control.controls[key];
                this.markAllAsTouchedAndDirty(childControl, isIgnoreEmptyValue, isTouched, isDirty);
            });

            // Update validity for the group/array
            control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }
    }

    public convertFileFormToBase64<T>(formGroup: FormGroup): Observable<T> {
        const keys = Object.keys(formGroup.controls); // Get all control keys

        const conversions: { [key: string]: Observable<any> } = {}; // Dynamic key-value observables

        for (const key of keys) {
            const control = formGroup.get(key);

            if (control instanceof FormControl) {
                // Handle FormControl with FileList
                const files = control.value as FileList | null;
                conversions[key] = this.fileService.convertFileListToBase64(files);
            } else if (control instanceof FormGroup) {
                // Recursively process nested FormGroup
                conversions[key] = this.convertFileFormToBase64(control);
            }
        }

        return forkJoin(conversions) as Observable<T>; // Combine results and cast to T
    }

    /**
     * Retrieves the name of a given control within its parent form group.
     *
     * @param {AbstractControl} control - The control whose name is to be determined.
     * @return {string | null} The name of the control if found, or null if the control
     *         is standalone or its name cannot be determined.
     */
    public getControlName(control: AbstractControl): string | null {
        const group = control.parent;
        if (!group) {
            return null;
        } // If the control has no parent, it's standalone

        return Object.keys(group.controls).find(name => control === group.get(name)) || null;
    }

    public getFormErrorsWithMessages(
        control: AbstractControl,
        errorKeyPrefix: string = '',
    ): { [controlPath: string]: string[] } {
        const errors: { [controlPath: string]: string[] } = {};

        if (control instanceof FormGroup || control instanceof FormArray) {
            Object.entries(control.controls).forEach(([key, childControl]) => {
                const childPath = errorKeyPrefix ? `${ errorKeyPrefix }.${ key }` : key;
                const childErrors = this.getFormErrorsWithMessages(childControl, childPath);
                Object.assign(errors, childErrors);
            });
        } else {
            if (control.errors) {
                const formatError = (err: unknown): string => {
                    if (typeof err === 'string') {return err;}

                    if (err === true) {return 'This field is required.';}

                    if (typeof err === 'object' && err !== null && 'message' in err) {
                        const msg = (err as { message?: string }).message;
                        return msg === '' ? 'This field is required.' : msg || JSON.stringify(err);
                    }

                    return JSON.stringify(err);
                };

                errors[errorKeyPrefix] = Object.values(control.errors).map(formatError);
            }
        }

        return errors;
    }

    public requiredValidatorIf(flag: ConditionalFlag): ValidatorFn {
        if (flag === null || flag === false) {
            return RxwebValidators.required({ conditionalExpression: () => false });
        }

        if (flag === true) {
            return RxwebValidators.required(); // unconditional
        }

        if (typeof flag === 'function') {
            return RxwebValidators.required({ conditionalExpression: flag });
        }

        return RxwebValidators.required({ conditionalExpression: () => false });
    }
}
